/**
 * Waveform synthesis engine
 * Pure functions that generate waveform samples based on vital parameters
 */

const SAMPLE_RATE = 250; // Hz, typical monitor sample rate

/**
 * ECG Lead II waveform — McSharry/PhysioNet Gaussian dipole model
 *
 * Based on:
 *   McSharry et al. (2003) "A Dynamical Model for Generating Synthetic
 *   Electrocardiogram Signals", IEEE Trans. Biomed. Eng.
 *   +
 *   PhysioNet ECG-PPG Simulator (Petrėnas et al. 2017, Sameni et al. 2007)
 *   https://physionet.org/content/ecg-ppg-simulator-arrhythmia/1.3.0/
 *
 * Each wave component (P, Q, R, S, T) is a Gaussian in the phase domain:
 *   ECG(θ) = Σᵢ aᵢ · exp( -(Δθᵢ)² / (2·bᵢ²) )
 *
 * where θ ∈ [-π, +π] sweeps one cardiac cycle and
 *   Δθᵢ = wrap(θ − θᵢ) to [−π, π].
 *
 * Parameters are derived from the PhysioNet 3-lead dipole model
 * (simPAF_DipoleGenerator.m) projected to Lead II via the Dower transform
 * row [0.235, 1.066, −0.132], with minor tuning for monitor aesthetics.
 */
export function generateECGWaveform(heartRate) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE; // samples per beat
  const numSamples = Math.ceil(beatDuration);
  const samples = new Float32Array(numSamples);

  // Gaussian parameters: [θᵢ (radians), aᵢ (amplitude), bᵢ (width, radians)]
  //
  //   θᵢ   — angular position within the cardiac cycle (−π to +π; 0 = R peak)
  //   aᵢ   — signed amplitude (negative for Q and S deflections)
  //   bᵢ   — Gaussian standard deviation in radians (controls width)
  //
  // Values adapted from McSharry (2003) Table I for Lead II,
  // with QRS widths tightened toward the PhysioNet model (bi ≈ 0.06–0.08)
  // for a sharper, more realistic monitor appearance.
  const waves = [
    // P wave — small upward deflection, atrial depolarisation
    { theta: -Math.PI / 3,   a:  1.2,   b: 0.25 },
    // Q wave — small downward deflection before R
    { theta: -Math.PI / 12,  a: -5.0,   b: 0.08 },
    // R wave — tall upward spike, ventricular depolarisation
    { theta:  0,             a:  30.0,  b: 0.08 },
    // S wave — downward deflection after R
    { theta:  Math.PI / 12,  a: -7.5,   b: 0.08 },
    // T wave — broad upward hump, ventricular repolarisation
    { theta:  Math.PI / 2,   a:  0.75,  b: 0.40 },
  ];

  // Pre-compute the waveform at each sample point
  let maxAbs = 0;
  for (let i = 0; i < numSamples; i++) {
    // Phase sweeps from −π to +π over one cardiac cycle
    const theta = -Math.PI + (i / numSamples) * 2 * Math.PI;

    let value = 0;
    for (let w = 0; w < waves.length; w++) {
      // Phase difference wrapped to [−π, π]
      let dtheta = theta - waves[w].theta;
      dtheta = dtheta - 2 * Math.PI * Math.round(dtheta / (2 * Math.PI));

      const bSq2 = 2 * waves[w].b * waves[w].b;
      value += waves[w].a * Math.exp(-(dtheta * dtheta) / bSq2);
    }

    samples[i] = value;
    if (Math.abs(value) > maxAbs) maxAbs = Math.abs(value);
  }

  // Normalise to ±1 range (R peak → +1, baseline → ~0)
  if (maxAbs > 0) {
    const scale = 1 / maxAbs;
    for (let i = 0; i < numSamples; i++) {
      samples[i] *= scale;
    }
  }

  return samples;
}

/**
 * Ventricular Tachycardia (VTach) — wide-complex, regular, fast rhythm
 *
 * Each beat is a Gaussian template: a broad R + deep discordant S (a wide,
 * notched QRS) followed by a broad, opposite-polarity (discordant) T wave, then
 * an isoelectric segment. There is deliberately NO P wave — AV dissociation
 * leaves no regular atrial deflection, and the flat pre-QRS segment avoids a
 * small bump reading as one. Baseline wander and per-sample noise are added so
 * the waveform never exactly repeats across cycles.
 *
 * @param {number} sampleRate    — samples per second
 * @param {number} bufferDuration — seconds of output
 * @param {number} heartRate     — beats per minute (clamped ≥ 100)
 * @returns {Float32Array}
 */
export function generateVTachBuffer(sampleRate, bufferDuration, heartRate) {
  const hr = Math.max(heartRate, 100);
  const numSamples = Math.ceil(sampleRate * bufferDuration);
  const buffer = new Float32Array(numSamples);

  const beatSamples = Math.max(1, Math.round((sampleRate * 60) / hr));
  const noiseLevel = 0.03;     // high-frequency electrical noise amplitude
  // Periodic (wrap-aware) Gaussian over the beat phase [0,1), so the tails join
  // smoothly across beat boundaries — no isoelectric gap, giving a continuous
  // undulation rather than isolated spikes.
  const pg = (x, c, w) => {
    let d = x - c;
    d = d - Math.round(d);     // nearest image → distance in [-0.5, 0.5)
    return Math.exp(-(d * d) / (2 * w * w));
  };

  // Monomorphic VT beat, expressed in phase p ∈ [0,1). One broad positive
  // deflection (wide QRS) merging into a broad, opposite-polarity (discordant)
  // deflection, spanning the whole beat with no flat segment → a wide-complex,
  // sine-wave-like appearance at fast rates. Deliberately NO P wave (AV
  // dissociation leaves no regular atrial deflection).
  // First pass: template + baseline wander (normalize before adding noise).
  for (let i = 0; i < numSamples; i++) {
    const p = (i % beatSamples) / beatSamples;
    const v =  1.00 * pg(p, 0.28, 0.135)   // broad R — wide complex
             - 0.80 * pg(p, 0.66, 0.150)   // broad discordant downstroke (S/T fused)
             + 0.04 * Math.sin(0.5 * Math.PI * (i / sampleRate)); // slow baseline wander
    buffer[i] = v;
  }

  // Normalize signal to ±1 before adding noise
  let maxAbs = 0;
  for (let i = 0; i < numSamples; i++) {
    if (Math.abs(buffer[i]) > maxAbs) maxAbs = Math.abs(buffer[i]);
  }
  if (maxAbs > 0) {
    for (let i = 0; i < numSamples; i++) buffer[i] /= maxAbs;
  }

  // Second pass: add noise after normalization so noise level stays calibrated
  for (let i = 0; i < numSamples; i++) {
    buffer[i] += (Math.random() - 0.5) * noiseLevel;
  }

  return buffer;
}

/**
 * Torsades de Pointes — polymorphic VT with characteristic "twisting" axis.
 *
 * Uses amplitude modulation: a slow sinusoidal envelope (the twist frequency)
 * modulates a smooth fast carrier, producing the hallmark QRS axis rotation.
 * When the envelope crosses zero the complexes invert — positive peaks become
 * negative troughs — reproducing the TdP "spindle" appearance.
 *
 * @param {number} sampleRate
 * @param {number} bufferDuration — seconds of output
 * @param {number} heartRate     — polymorphic rate (clamped ≥ 180)
 * @returns {Float32Array}
 */
export function generateTdPBuffer(sampleRate, bufferDuration, heartRate) {
  const hr = Math.max(heartRate, 180);
  const numSamples = Math.ceil(sampleRate * bufferDuration);
  const buffer = new Float32Array(numSamples);

  const fc = hr / 60.0;   // carrier: fast QRS rate in Hz
  const fm = 0.15;         // envelope: ~one full twist every 6–7 s
  const noiseLevel = 0.06;

  // First pass: signal + baseline wander (normalize before adding noise)
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const envelope = Math.sin(2 * Math.PI * fm * t);
    const carrierPhase = 2 * Math.PI * fc * t;
    // Small 2nd harmonic smooths the sharp carrier spike into rounded complexes
    const smoothCarrier = Math.sin(carrierPhase) + 0.15 * Math.sin(2 * carrierPhase);
    buffer[i] = envelope * smoothCarrier
              + 0.05 * Math.sin(0.5 * Math.PI * t); // baseline wander
  }

  // Normalize to ±1
  let maxAbs = 0;
  for (let i = 0; i < numSamples; i++) {
    if (Math.abs(buffer[i]) > maxAbs) maxAbs = Math.abs(buffer[i]);
  }
  if (maxAbs > 0) {
    for (let i = 0; i < numSamples; i++) buffer[i] /= maxAbs;
  }

  // Add noise after normalization so level stays calibrated
  for (let i = 0; i < numSamples; i++) {
    buffer[i] += (Math.random() - 0.5) * noiseLevel;
  }

  return buffer;
}

/**
 * Smooth step (sigmoid) between two phase boundaries.
 * Returns 0 below `start`, 1 above `end`, smooth S-curve between.
 * Steepness k controls the transition sharpness.
 */
function smoothMesa(theta, start, end, k = 30) {
  const rise = 1 / (1 + Math.exp(-k * (theta - start)));
  const fall = 1 / (1 + Math.exp(-k * (theta - end)));
  return rise - fall;
}

/**
 * Generate base McSharry ECG Lead II and return raw (un-normalised) samples.
 * Shared by both ST-change generators.
 */
function generateBaseECG(numSamples, waves) {
  const samples = new Float32Array(numSamples);
  for (let i = 0; i < numSamples; i++) {
    const theta = -Math.PI + (i / numSamples) * 2 * Math.PI;
    let value = 0;
    for (let w = 0; w < waves.length; w++) {
      let dtheta = theta - waves[w].theta;
      dtheta = dtheta - 2 * Math.PI * Math.round(dtheta / (2 * Math.PI));
      value += waves[w].a * Math.exp(-(dtheta * dtheta) / (2 * waves[w].b * waves[w].b));
    }
    samples[i] = value;
  }
  return samples;
}

/** Normalise a Float32Array to ±1 in-place and return it. */
function normaliseWaveform(samples) {
  let maxAbs = 0;
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]) > maxAbs) maxAbs = Math.abs(samples[i]);
  }
  if (maxAbs > 0) {
    const scale = 1 / maxAbs;
    for (let i = 0; i < samples.length; i++) samples[i] *= scale;
  }
  return samples;
}

/**
 * ECG Lead II with ST segment depression
 *
 * Approach: generate the normal McSharry P-QRS-T, then apply a negative
 * "mesa" (smoothed step) offset between the J-point (end of S wave, θ≈0.3)
 * and the T-wave onset (θ≈1.0). This produces a horizontal/downsloping
 * ST depression of ~2 mm equivalent — classic ischaemia / demand pattern.
 *
 * The mesa uses sigmoid transitions (k=30) at both edges so there are no
 * discontinuities that would look like artefact on the scrolling display.
 */
export function generateSTDepressionWaveform(heartRate) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);

  // Standard McSharry Lead II Gaussians
  const waves = [
    { theta: -Math.PI / 3,   a:  1.2,   b: 0.25 },  // P
    { theta: -Math.PI / 12,  a: -5.0,   b: 0.08 },  // Q
    { theta:  0,             a:  30.0,  b: 0.08 },  // R
    { theta:  Math.PI / 12,  a: -7.5,   b: 0.08 },  // S
    { theta:  Math.PI / 2,   a:  0.75,  b: 0.40 },  // T
  ];

  const samples = generateBaseECG(numSamples, waves);

  // ST depression mesa: J-point (θ≈0.3) → late repolarisation (θ≈1.70)
  // tOnset extended to 1.70 (double the original 0.70 rad span) so the
  // depressed plateau covers the full ST segment and T-wave region —
  // typical of significant subendocardial ischaemia where the T wave is
  // buried within the prolonged depressed segment.
  const stLevel = -3.0;
  const jPoint = 0.30;    // just after S wave (θ = π/12 ≈ 0.26)
  const tOnset = 1.70;    // extended: 2× original span (was 1.00)

  for (let i = 0; i < numSamples; i++) {
    const theta = -Math.PI + (i / numSamples) * 2 * Math.PI;
    samples[i] += stLevel * smoothMesa(theta, jPoint, tOnset);
  }

  return normaliseWaveform(samples);
}

/**
 * ECG Lead II with ST segment elevation (STEMI pattern)
 *
 * McSharry Gaussian-sum model extended with a 6th Gaussian for the ST
 * injury current, placed at θ_ST = (θ_S + θ_T)/2 ≈ 0.916 rad per the
 * McSharry ODE extension formulation.
 *
 * Parameter rationale:
 *   S  a=−5.5 b=0.08 : visible S trough below isoelectric (J-point effect)
 *   ST a=+4.5 b=0.38 : ~15% of R-wave height — clinically significant but
 *                       QRS still dominates the display as in the reference
 *                       image. b narrowed from formula (0.655) to 0.38 so
 *                       the Gaussian tail doesn't fill the S trough.
 *   T  a=+2.2 b=0.40 : hyperacute T — taller than normal, distinct peak
 */
export function generateSTElevationWaveform(heartRate) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);

  // θ_ST = (θ_S + θ_T) / 2 = (π/12 + π/2) / 2 ≈ 0.916 rad
  const waves = [
    { theta: -Math.PI / 3,   a:  1.2,   b: 0.25 },  // P
    { theta: -Math.PI / 12,  a: -5.0,   b: 0.09 },  // Q
    { theta:  0,             a:  30.0,  b: 0.08 },  // R
    { theta:  Math.PI / 12,  a: -5.5,   b: 0.08 },  // S (visible trough)
    { theta:  0.916,         a:  4.5,   b: 0.38 },  // ST elevation — ~15% of R, J-point elevated
    { theta:  Math.PI / 2,   a:  2.2,   b: 0.40 },  // T (hyperacute)
  ];

  return normaliseWaveform(generateBaseECG(numSamples, waves));
}

/**
 * SpO2 plethysmogram: smooth sine with dicrotic notch
 */
export function generateSpO2Waveform(heartRate, spo2Value) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);
  const samples = new Float32Array(numSamples);

  // Amplitude scales with SpO2 (lower spo2 = lower amplitude)
  const amplitudeScale = Math.max(0.2, spo2Value / 100);

  for (let i = 0; i < numSamples; i++) {
    const phase = (i / beatDuration) * Math.PI * 2;

    // Main systolic peak
    let amplitude = amplitudeScale * Math.sin(phase);

    // Dicrotic notch: small secondary peak
    if (phase > Math.PI * 0.5 && phase < Math.PI * 1.8) {
      const notchPhase = (phase - Math.PI * 0.8) / 0.5;
      amplitude += amplitudeScale * 0.3 * Math.sin(notchPhase) * Math.exp(-Math.pow(notchPhase / 0.5, 2));
    }

    // Smooth decay to baseline
    amplitude *= Math.exp(-Math.pow((phase - Math.PI * 0.5) / 1.5, 2) * 0.5);

    samples[i] = amplitude;
  }

  return samples;
}

/**
 * Capnography — composite mathematical model
 *
 * Four-phase breath cycle with realistic transition shapes:
 *
 *   Phase I   (0–10%):  Baseline — inspiration / dead-space gas (CO₂ ≈ 0)
 *   Phase II  (10–20%): Sigmoidal rise — mixing of dead-space and alveolar gas
 *   Phase III (20–60%): Alveolar plateau — slight upward tilt to EtCO₂ peak
 *   Phase IV  (60–100%): Exponential downstroke — inspiratory washout
 *
 * The sigmoidal Phase II and exponential Phase IV produce smooth, rounded
 * transitions instead of hard corners — matching real clinical capnography.
 *
 * Phase proportions adapted from:
 *   generate_etco2_trace() composite model (p1=0.1, p2=0.1, p3=0.4, p4=0.4)
 */
export function generateCapnographyWaveform(respiratoryRate, etco2Value) {
  const breathDuration = (60 / Math.max(respiratoryRate, 1)) * SAMPLE_RATE;
  const numSamples = Math.ceil(breathDuration);
  const samples = new Float32Array(numSamples);

  // Phase boundaries (fraction of breath cycle)
  const p1 = 0.10; // dead-space baseline
  const p2 = 0.10; // sigmoidal upstroke
  const p3 = 0.40; // alveolar plateau
  // p4 = 0.40     // exponential downstroke (remainder)

  for (let i = 0; i < numSamples; i++) {
    const phase = i / numSamples;
    let norm = 0; // 0 = baseline (CO₂ = 0), 1 = EtCO₂ peak

    if (phase < p1) {
      // Phase I: flat baseline (inspiration / dead space)
      norm = 0;
    } else if (phase < p1 + p2) {
      // Phase II: sigmoidal rise (dead-space → alveolar gas mixing)
      const progress = (phase - p1) / p2;
      const sigmoid = 1 / (1 + Math.exp(-10 * (progress - 0.5)));
      norm = 0.8 * sigmoid;
    } else if (phase < p1 + p2 + p3) {
      // Phase III: alveolar plateau with slight upward tilt
      const progress = (phase - p1 - p2) / p3;
      norm = 0.8 + 0.2 * progress;
    } else {
      // Phase IV: exponential inspiratory downstroke
      const progress = (phase - p1 - p2 - p3) / (1 - p1 - p2 - p3);
      norm = Math.exp(-15 * progress);
    }

    // Map to canvas space: baseline (norm=0) → bottom, peak (norm=1) → top.
    // Canvas renders y = centerY - sample * (centerY * 0.78),
    // so sample = -0.85 → near bottom, sample = +0.85 → near top.
    samples[i] = Math.max(0, norm) * 1.7 - 0.85;
  }

  return samples;
}

/**
 * Capnography — Rebreathing pattern
 *
 * Identical to the normal four-phase model except that Phase I (the
 * inspiratory baseline) is elevated above zero.  Clinically this indicates
 * that the patient is inspiring CO₂-containing gas — caused by an exhausted
 * CO₂ absorbent canister, a faulty expiratory valve, or inadequate fresh-gas
 * flow.  The baseline is set to 30% of the EtCO₂ peak (norm = 0.30), giving
 * a visible "floating baseline" that immediately distinguishes this trace
 * from normal capnography.
 */
export function generateRebreathingCapno(respiratoryRate, etco2Value) {
  const breathDuration = (60 / Math.max(respiratoryRate, 1)) * SAMPLE_RATE;
  const numSamples = Math.ceil(breathDuration);
  const samples = new Float32Array(numSamples);

  const BASE = 0.30; // inspired CO₂ fraction (elevated baseline)
  const p1 = 0.10;
  const p2 = 0.10;
  const p3 = 0.40;

  for (let i = 0; i < numSamples; i++) {
    const phase = i / numSamples;
    let norm;

    if (phase < p1) {
      norm = BASE; // baseline is elevated — rebreathed CO₂
    } else if (phase < p1 + p2) {
      const progress = (phase - p1) / p2;
      const sigmoid = 1 / (1 + Math.exp(-10 * (progress - 0.5)));
      norm = BASE + (0.8 - BASE) * sigmoid;
    } else if (phase < p1 + p2 + p3) {
      const progress = (phase - p1 - p2) / p3;
      norm = 0.8 + 0.2 * progress;
    } else {
      const progress = (phase - p1 - p2 - p3) / (1 - p1 - p2 - p3);
      // Downstroke decays back to BASE (not zero)
      norm = BASE + (1.0 - BASE) * Math.exp(-15 * progress);
    }

    samples[i] = Math.max(0, norm) * 1.7 - 0.85;
  }

  return samples;
}

/**
 * Capnography — Curare Cleft (neuromuscular blockade wearing off)
 *
 * Normal P-QRS-T-shaped capnogram with a sudden notch (Gaussian dip) at
 * approximately 75% through the alveolar plateau (Phase III).  This dip
 * represents a spontaneous inspiratory effort by the patient fighting the
 * mechanical ventilator — a sign that neuromuscular blockade is dissipating.
 *
 * The cleft depth is set to 50% of the plateau amplitude, which is visible
 * but does not return the trace to baseline (a full return would indicate a
 * complete breath, not a cleft).
 */
export function generateCurareCleftCapno(respiratoryRate, etco2Value) {
  const breathDuration = (60 / Math.max(respiratoryRate, 1)) * SAMPLE_RATE;
  const numSamples = Math.ceil(breathDuration);
  const samples = new Float32Array(numSamples);

  const p1 = 0.10;
  const p2 = 0.10;
  const p3 = 0.40;

  // Cleft: centred at 75% of Phase III
  const cleftCentre = p1 + p2 + p3 * 0.75;
  const cleftSigma  = 0.025; // narrow Gaussian
  const cleftDepth  = 0.50;  // 50% of peak amplitude dip

  for (let i = 0; i < numSamples; i++) {
    const phase = i / numSamples;
    let norm;

    if (phase < p1) {
      norm = 0;
    } else if (phase < p1 + p2) {
      const progress = (phase - p1) / p2;
      const sigmoid = 1 / (1 + Math.exp(-10 * (progress - 0.5)));
      norm = 0.8 * sigmoid;
    } else if (phase < p1 + p2 + p3) {
      const progress = (phase - p1 - p2) / p3;
      norm = 0.8 + 0.2 * progress;
    } else {
      const progress = (phase - p1 - p2 - p3) / (1 - p1 - p2 - p3);
      norm = Math.exp(-15 * progress);
    }

    // Superimpose the cleft dip (only visible during Phase III)
    const dp = phase - cleftCentre;
    const cleft = cleftDepth * Math.exp(-(dp * dp) / (2 * cleftSigma * cleftSigma));
    norm = Math.max(0, norm - cleft);

    samples[i] = norm * 1.7 - 0.85;
  }

  return samples;
}

/**
 * Capnography — Obstructed / Shark-Fin pattern
 *
 * Models bronchospasm, COPD, or a kinked ETT. Alveoli empty unevenly —
 * obstructed units take longer to deliver CO₂, prolonging the expiratory
 * upstroke and eliminating the distinct alveolar plateau.
 *
 * Implementation:
 *   • Phase II (upstroke) occupies 40% of the cycle (vs. 10% in normal).
 *     The sigmoid steepness drops to k = 3, giving a very gradual,
 *     almost-linear rise that dominates the waveform.
 *   • Phase III (plateau) is truncated to 15% — the trace barely plateaus
 *     before the downstroke begins.
 *   • The result is a triangular / shark-fin shape: a slow, prolonged rise
 *     to a rounded peak, then a rapid fall — no flat top.
 */
export function generateObstructedCapno(respiratoryRate, etco2Value) {
  const breathDuration = (60 / Math.max(respiratoryRate, 1)) * SAMPLE_RATE;
  const numSamples = Math.ceil(breathDuration);
  const samples = new Float32Array(numSamples);

  const p1 = 0.08;  // shortened inspiration
  const p2 = 0.40;  // prolonged, slow upstroke (was 0.10)
  const p3 = 0.15;  // abbreviated plateau (was 0.40)
  // p4 = 0.37 — remainder

  for (let i = 0; i < numSamples; i++) {
    const phase = i / numSamples;
    let norm;

    if (phase < p1) {
      norm = 0;
    } else if (phase < p1 + p2) {
      // Slow sigmoid (k=3): prolonged expiratory upstroke
      const progress = (phase - p1) / p2;
      const sigmoid = 1 / (1 + Math.exp(-3 * (progress - 0.5)));
      norm = 0.85 * sigmoid;
    } else if (phase < p1 + p2 + p3) {
      // Short rounded plateau — slight rise to peak
      const progress = (phase - p1 - p2) / p3;
      norm = 0.85 + 0.15 * progress;
    } else {
      // Rapid downstroke
      const progress = (phase - p1 - p2 - p3) / (1 - p1 - p2 - p3);
      norm = Math.exp(-20 * progress);
    }

    samples[i] = Math.max(0, norm) * 1.7 - 0.85;
  }

  return samples;
}

/**
 * Arterial Blood Pressure (ABP) — Piecewise Elastance + Windkessel model
 *
 * Physiologically accurate three-phase model:
 *
 *   Phase 1 — Rapid systolic upstroke (0 → T_up, ≈10% of cycle):
 *     P(t) = P_dia + PP · sin²(πt / (2·T_up))
 *     Models fast ventricular ejection; sin² provides a smooth,
 *     steep rise matching the high contractility upstroke of radial A-line.
 *
 *   Phase 2 — Systolic decline (T_up → T_sys, ≈10–35% of cycle):
 *     P(t) = P_sys + (P_notch − P_sys) · progress
 *     Linear fall from peak to notch pressure, representing the gradual
 *     pressure drop as ventricular ejection decelerates.
 *
 *   Phase 3 — Diastolic runoff (T_sys → T, ≈35–100% of cycle):
 *     P(t) = P_dia + (P_notch − P_dia) · e^(−(t−T_sys) / RC)
 *     Two-element Windkessel: R = peripheral resistance, C = arterial
 *     compliance. RC tuned so decay ≈ 95% complete by next beat.
 *
 *   Dicrotic notch: Gaussian dip (σ ≈ 1.5% of cycle, depth ≈ 12% PP)
 *     centred at T_sys (aortic valve closure) and superimposed on all phases.
 *
 * References:
 *   PMC9114050 — piecewise elastance + Windkessel model
 *   derangedphysiology.com — A-line resonance & waveform components
 *   Sagepub 10.1177/154431671403800306 — dicrotic notch physiology
 */
/**
 * IABP-augmented arterial waveform (1:1 assist).
 *
 * Balloon inflates at the dicrotic notch → a diastolic augmentation peak
 * HIGHER than the native systolic peak (the IABP signature), then deflates
 * just before the next systole → a low assisted end-diastolic dip that
 * reduces afterload. Modelled in mmHg then MAP-centred like the native
 * ABP trace so it tiles per-beat and cross-fades cleanly.
 */
export function generateIABPWaveform(heartRate, systolic, diastolic) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);
  const samples = new Float32Array(numSamples);

  const pp = Math.max(systolic - diastolic, 1);
  const map = diastolic + pp / 3;
  const augPeak = systolic + 0.35 * pp;        // augmentation ABOVE native systole
  const assistedEndDia = diastolic - 0.25 * pp; // deflation dip before next beat
  const notch = diastolic + 0.55 * pp;

  const T_up = Math.round(0.09 * numSamples);   // systolic upstroke
  const T_sys = Math.round(0.30 * numSamples);  // systole → notch
  const T_augPk = Math.round(0.48 * numSamples);// balloon inflation peak
  const T_augEnd = Math.round(0.80 * numSamples);// augmentation runoff
  const gauss = (i, c, w) => Math.exp(-((i - c) * (i - c)) / (2 * w * w));

  for (let i = 0; i < numSamples; i++) {
    let pressure;
    if (i <= T_up) {
      const t = i / T_up;
      const sv = Math.sin(Math.PI * t / 2);
      pressure = assistedEndDia + (systolic - assistedEndDia) * sv * sv;
    } else if (i <= T_sys) {
      const t = (i - T_up) / (T_sys - T_up);
      pressure = systolic + (notch - systolic) * t;
    } else if (i <= T_augPk) {
      // Balloon inflation: rapid rise to the augmentation peak
      const t = (i - T_sys) / (T_augPk - T_sys);
      const sv = Math.sin(Math.PI * t / 2);
      pressure = notch + (augPeak - notch) * sv * sv;
    } else if (i <= T_augEnd) {
      // Augmentation runoff
      const t = (i - T_augPk) / (T_augEnd - T_augPk);
      pressure = augPeak + (diastolic - augPeak) * t * t;
    } else {
      // Balloon deflation: sharp drop to the assisted end-diastolic dip
      const t = (i - T_augEnd) / Math.max(1, numSamples - T_augEnd);
      pressure = diastolic + (assistedEndDia - diastolic) * Math.sin(Math.PI * t / 2);
    }
    // Small notch artifact at balloon inflation
    pressure -= 0.05 * pp * gauss(i, T_sys, 0.012 * numSamples);
    samples[i] = (pressure - map) / (pp * 0.75);
  }
  return samples;
}

export function generateABPWaveform(heartRate, systolic, diastolic) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);
  const samples = new Float32Array(numSamples);

  const pp  = Math.max(systolic - diastolic, 1); // pulse pressure (mmHg)
  const map = diastolic + pp / 3;                // MAP (1/3 rule)

  // ── Timing (in samples) ────────────────────────────────────────────────
  const T_up  = Math.round(0.10 * numSamples); // end of rapid upstroke (10%)
  const T_sys = Math.round(0.35 * numSamples); // end of systole / notch (35%)

  // ── Pressure landmarks ─────────────────────────────────────────────────
  // Dicrotic notch sits at ~65% of pulse pressure above diastolic (clinically
  // measured as ~70–80% of systolic on a radial trace after wave reflection).
  const P_notch = diastolic + 0.65 * pp;

  // ── Windkessel RC ─────────────────────────────────────────────────────
  // Solve for RC so that after the remaining diastolic interval (T_dia samples)
  // the decay is 95% complete: e^(−T_dia/RC) = 0.05 → RC = T_dia / ln(20)
  const T_dia = numSamples - T_sys;
  const RC = T_dia / Math.log(20);

  // ── Dicrotic notch parameters ─────────────────────────────────────────
  const notchDepth  = 0.12 * pp;               // depth ≈ 12% of pulse pressure
  const notchSigmaS = 0.015 * numSamples;      // Gaussian σ ≈ 1.5% of cycle
  const notchSigma2 = 2 * notchSigmaS * notchSigmaS;

  for (let i = 0; i < numSamples; i++) {
    let pressure;

    if (i <= T_up) {
      // Phase 1: rapid systolic upstroke (sin² ramp)
      const t = i / T_up;
      const sinVal = Math.sin(Math.PI * t / 2);
      pressure = diastolic + pp * sinVal * sinVal;

    } else if (i <= T_sys) {
      // Phase 2: systolic decline (linear, peak → notch level)
      const t = (i - T_up) / (T_sys - T_up);
      pressure = systolic + (P_notch - systolic) * t;

    } else {
      // Phase 3: diastolic runoff — Windkessel exponential decay
      pressure = diastolic + (P_notch - diastolic) * Math.exp(-(i - T_sys) / RC);
    }

    // Superimpose dicrotic notch (Gaussian dip at aortic valve closure)
    const dn = i - T_sys;
    pressure -= notchDepth * Math.exp(-(dn * dn) / notchSigma2);

    // Convert mmHg → canvas units centred on MAP
    // Canvas: sample=0 → centreline; ±1 → edge of channel.
    // Dividing by (pp * 0.75) maps systolic peak to ≈+0.88 of half-height.
    samples[i] = (pressure - map) / (pp * 0.75);
  }

  return samples;
}

/**
 * Central Venous Pressure (CVP): low-amplitude a-c-v wave pattern
 */
export function generateCVPWaveform(heartRate, cvpValue) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);
  const samples = new Float32Array(numSamples);

  const amplitude = (cvpValue / 10) * 0.3; // small amplitude

  for (let i = 0; i < numSamples; i++) {
    const phase = (i / beatDuration) * Math.PI * 2;

    // 'a' wave: atrial contraction
    let value = amplitude * Math.sin(phase) * Math.exp(-Math.pow((phase - Math.PI * 0.2) / 0.2, 2));

    // 'c' wave: ventricular contraction
    value += amplitude * 0.6 * Math.sin(phase - Math.PI * 0.6) * Math.exp(-Math.pow((phase - Math.PI * 0.8) / 0.15, 2));

    // 'v' wave: venous filling
    value += amplitude * 0.4 * Math.sin(phase - Math.PI * 1.3) * Math.exp(-Math.pow((phase - Math.PI * 1.5) / 0.2, 2));

    samples[i] = value;
  }

  return samples;
}

/**
 * Ventricular Fibrillation (VF) waveform
 *
 * VF is chaotic electrical activity with no organised QRS complexes.
 * Modelled as a sum of sinusoids at typical VF frequencies (3–9 Hz)
 * with a slow amplitude modulation (0.3–0.8 Hz) that produces the
 * characteristic "coarse VF" waxing-and-waning envelope.
 *
 * A seeded PRNG (xorshift32) is used so that:
 *   • the waveform is deterministic for a given seed (no jarring jumps
 *     when the buffer is regenerated on a vitals change)
 *   • but still looks random/chaotic to the viewer
 */
/**
 * SVT ECG — narrow-complex, regular tachycardia.
 * P waves are absent (buried in the preceding T wave or retrograde).
 * QRS morphology is identical to normal sinus; only the P is removed.
 */
export function generateSVTWaveform(heartRate) {
  const hr = Math.max(heartRate, 120);
  const numSamples = Math.ceil((60 / hr) * SAMPLE_RATE);

  // No P wave — Q, R, S, T only
  const waves = [
    { theta: -Math.PI / 12, a: -5.0,  b: 0.08 }, // Q
    { theta:  0,            a:  30.0, b: 0.08 }, // R
    { theta:  Math.PI / 12, a: -7.5,  b: 0.08 }, // S
    { theta:  Math.PI / 2,  a:  0.65, b: 0.36 }, // T (slightly attenuated at fast rate)
  ];
  return normaliseWaveform(generateBaseECG(numSamples, waves));
}

/**
 * AFib ECG buffer — whole-buffer generation (like VF) because the rhythm is
 * irregularly irregular.
 *
 * The buffer contains:
 *   • A low-amplitude fibrillatory baseline (f-waves, ~6–12 Hz) — no P waves
 *   • Narrow QRS complexes stamped at jittered intervals (±20% of mean cycle)
 */
export function generateAFibBuffer(sampleRate, bufferDuration, hr) {
  const N = sampleRate * bufferDuration;
  const buffer = new Float32Array(N);

  // Deterministic PRNG — reproducible "random" beat timing
  let seed = 0x12A4C8F0;
  const rand = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return (seed >>> 0) / 0xFFFFFFFF;
  };

  // Fibrillatory baseline: two f-wave sinusoids at slightly different frequencies
  // to create the characteristic wandering undulation (~6–10 Hz on a 10-second strip)
  const fFreq1 = 7.2, fFreq2 = 11.6;
  for (let i = 0; i < N; i++) {
    const t = i / sampleRate;
    buffer[i] = 0.07 * Math.sin(2 * Math.PI * fFreq1 * t)
              + 0.04 * Math.sin(2 * Math.PI * fFreq2 * t + 1.3);
  }

  // Narrow QRS template (no P wave) — same Gaussians as SVT
  const templateSamples = Math.ceil((60 / Math.max(hr, 60)) * sampleRate);
  const waves = [
    { theta: -Math.PI / 12, a: -5.0,  b: 0.08 },
    { theta:  0,            a:  30.0, b: 0.08 },
    { theta:  Math.PI / 12, a: -7.5,  b: 0.08 },
    { theta:  Math.PI / 2,  a:  0.65, b: 0.38 },
  ];
  const qrs = normaliseWaveform(generateBaseECG(templateSamples, waves));

  // Stamp QRS beats at irregularly irregular intervals
  const meanInterval = (60 / hr) * sampleRate;
  let pos = Math.round(rand() * meanInterval * 0.5); // stagger first beat

  while (pos < N) {
    for (let k = 0; k < qrs.length; k++) {
      if (pos + k < N) buffer[pos + k] += qrs[k];
    }
    // Next interval: ±20% jitter around mean — creates the irregularity
    const jitter = rand() * 0.4 - 0.2;
    pos += Math.round(meanInterval * (1 + jitter));
  }

  return buffer;
}

export function generateVFBuffer(sampleRate, bufferDuration) {
  const N = sampleRate * bufferDuration;
  const buffer = new Float32Array(N);

  // Simple xorshift32 PRNG for deterministic "randomness"
  let seed = 0xDEADBEEF;
  const rand = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return ((seed >>> 0) / 0xFFFFFFFF) * 2 - 1; // [-1, 1]
  };

  // VF component frequencies (Hz) — typical dominant 3–9 Hz range
  const freqs = [3.2, 4.7, 5.8, 7.1, 8.4];
  // Random-ish phases (deterministic from PRNG)
  const phases = freqs.map(() => rand() * Math.PI);
  // Amplitude weights — lower frequencies dominate (coarse VF)
  const amps = [0.35, 0.28, 0.18, 0.12, 0.07];

  // Slow amplitude-modulation envelope (waxing/waning at ~0.4 Hz)
  const envFreq = 0.4;
  const envPhase = rand() * Math.PI;

  for (let i = 0; i < N; i++) {
    const t = i / sampleRate;

    // Envelope: oscillates between ~0.25 and 1.0
    const env = 0.55 + 0.45 * Math.sin(2 * Math.PI * envFreq * t + envPhase);

    // Sum of sinusoids at VF frequencies
    let signal = 0;
    for (let f = 0; f < freqs.length; f++) {
      signal += amps[f] * Math.sin(2 * Math.PI * freqs[f] * t + phases[f]);
    }

    // Add a small high-frequency noise component for texture
    signal += 0.06 * rand();

    buffer[i] = signal * env * 0.85;
  }

  return buffer;
}

/**
 * Generate a flat (zero) buffer — used for pulseless channels during VF/arrest
 */
function generateFlatlineBuffer(sampleRate, bufferDuration) {
  return new Float32Array(sampleRate * bufferDuration);
}

/**
 * ECG Lead II — Severe Hypothermia (core temp < 28 °C)
 *
 * The hallmark finding is the Osborn (J) wave — a prominent dome-shaped
 * positive deflection at the J point, encoded here as a dedicated Gaussian
 * centred at θ ≈ 0.55 (between S-wave end and T-wave onset).
 *
 * Additional morphological changes from slowed conduction:
 *
 *   1. Prolonged PR interval — P-wave Gaussian shifted from θ = −π/3 (−1.05)
 *      to θ = −π/4 (−0.79), reducing the P-to-R phase gap.
 *
 *   2. Wide QRS — intraventricular conduction slowed by cold.
 *      QRS Gaussians widened: b 0.08 → 0.16.
 *
 *   3. Osborn (J) wave — dome at θ = 0.55, a = 7.0, b = 0.15.
 *      After R-peak normalisation: Osborn/R ≈ 0.58 — visually prominent.
 *
 *   4. Prolonged QT — T wave shifted later (θ = π × 0.60 ≈ 1.88 vs normal
 *      π/2 ≈ 1.57) and widened (b 0.40 → 0.50).
 *
 * The profound bradycardia itself is expressed through the HR vital (28 bpm),
 * which stretches the cycle duration — all intervals widen proportionally.
 *
 * References:
 *   Vassallo SU et al. (1999) — "Osborn wave in hypothermia", Ann Emerg Med
 *   Mattu A et al. (2002) — ECG manifestations of hypothermia
 */
export function generateHypothermiaWaveform(heartRate) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);

  const waves = [
    // P wave — present but prolonged PR (shifted toward R)
    { theta: -Math.PI / 4,   a:  1.0,  b: 0.30 },
    // Q wave — widened QRS
    { theta: -Math.PI / 12,  a: -3.0,  b: 0.16 },
    // R wave — reduced amplitude (wide complex distributes energy)
    { theta:  0,             a: 12.0,  b: 0.16 },
    // S wave — widened
    { theta:  Math.PI / 12,  a: -4.5,  b: 0.16 },
    // Osborn (J) wave — hallmark of hypothermia; dome at J-point
    { theta:  0.55,          a:  7.0,  b: 0.15 },
    // T wave — broad; shifted later for prolonged QT
    { theta:  Math.PI * 0.60, a: 0.8,  b: 0.50 },
  ];

  const samples = generateBaseECG(numSamples, waves);
  return normaliseWaveform(samples);
}

/**
 * ECG Lead II — Hyperkalemia pattern (moderate, K⁺ ≈ 6.5–7.5 mEq/L)
 *
 * Matches the classic teaching picture: strikingly tall, narrow, sharply
 * POINTED ("tented") T waves with small/indiscernible P waves — while the QRS
 * is still only mildly widened (marked QRS broadening and the sine-wave pattern
 * belong to more severe K⁺). Encoded in the Gaussian parameters:
 *
 *   1. P wave absent — atrial paralysis: SA node suppression prevents reliable
 *      atrial depolarisation at high extracellular [K⁺].  P Gaussian removed.
 *
 *   2. Mildly widened QRS — early intraventricular conduction delay. Only a
 *      slight broadening at this stage (b 0.08 → 0.11); the QRS stays narrow and
 *      sharp. Gross widening / sine-wave morphology is reserved for severe K⁺.
 *
 *   3. Peaked ("tented") T wave — accelerated repolarisation shortens Phase 3,
 *      producing a tall, NARROW, sharply pointed T. Encoded as a large-amplitude,
 *      small-width Gaussian (a 0.75 → 16.0, b 0.40 → 0.13) so after R-peak
 *      normalisation the T reaches ≈ 0.6 of R height with a distinct point —
 *      visually the dominant feature, matching the classic teaching tracing.
 *
 * References:
 *   Mattu A et al. (2000) "Widespread ST depression and T-wave inversion"
 *   Diercks DB et al. — ECG changes in hyperkalemia (Emerg Med Clin)
 */
// Normal-sinus and full-hyperkalemic endpoints for each Gaussian wave. The
// active morphology is a per-parameter linear blend between them, so a
// `progress` of 0 renders an ordinary Lead II beat and 1 the full hyperkalemic
// pattern; intermediate values show the P wave fading and the T wave tenting.
const HYPERK_ENDPOINTS = [
  // [theta,           normal {a,b},        hyperK {a,b}]
  { theta: -Math.PI / 3,  n: { a: 1.2,  b: 0.25 }, k: { a: 0.0,  b: 0.25 } }, // P → fades out
  { theta: -Math.PI / 12, n: { a: -5.0, b: 0.08 }, k: { a: -3.5, b: 0.11 } }, // Q → mild widening
  { theta:  0,            n: { a: 30.0, b: 0.08 }, k: { a: 26.0, b: 0.11 } }, // R
  { theta:  Math.PI / 12, n: { a: -7.5, b: 0.08 }, k: { a: -5.0, b: 0.11 } }, // S
  { theta:  Math.PI / 2,  n: { a: 0.75, b: 0.40 }, k: { a: 16.0, b: 0.13 } }, // T → tall, narrow, tented
];

/**
 * Hyperkalemia ECG at a given severity.
 * @param {number} heartRate
 * @param {number} progress  0 = normal sinus … 1 = full hyperkalemic pattern
 */
export function generateHyperkalemiaWaveform(heartRate, progress = 1) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);
  const p = Math.max(0, Math.min(1, progress));
  const lerp = (a, b) => a + (b - a) * p;

  const waves = HYPERK_ENDPOINTS.map((w) => ({
    theta: w.theta,
    a: lerp(w.n.a, w.k.a),
    b: lerp(w.n.b, w.k.b),
  }));

  const samples = generateBaseECG(numSamples, waves);
  return normaliseWaveform(samples);
}

/**
 * ECG Lead II — Ventricularly paced (transcutaneous / transvenous pacemaker)
 *
 * Three hallmarks of a paced beat:
 *
 *   1. Pacing spike — a very narrow, tall artefact immediately before the QRS.
 *      Transcutaneous pads produce a large surface artifact (amplitude ~1.5×
 *      after normalisation so it remains visible on the scrolling trace).
 *      Encoded as a very narrow Gaussian (b = 0.03) at θ = −π/6.5 (just ahead
 *      of the QRS complex).
 *
 *   2. Wide QRS, LBBB-like morphology in Lead II — ventricular stimulation from
 *      the right ventricle epicardium forces depolarisation through myocardium
 *      rather than the His-Purkinje system (slow cell-to-cell conduction).
 *      Result: broad single R wave (b = 0.22, ~2.75× normal), no Q, no S.
 *      Lead II shows a dominant upright complex (positive concordance).
 *
 *   3. Discordant T wave — repolarisation is abnormal when depolarisation is
 *      abnormal. The T wave is inverted (negative) and broad, opposite in
 *      polarity to the QRS. Classic feature used to distinguish paced rhythm
 *      from ischaemia on a 12-lead.
 *
 * No P wave is encoded — the pacemaker fires asynchronously with any residual
 * atrial activity, and at typical pacing rates the P is buried in the QRS.
 *
 * References:
 *   Garcia TB & Holtz NE (2001) — 12-Lead ECG: The Art of Interpretation
 *   Harrigan RA et al. (2002) — ECG abnormalities with cardiac pacing
 */
export function generatePacedECGWaveform(heartRate) {
  const beatDuration = (60 / heartRate) * SAMPLE_RATE;
  const numSamples = Math.ceil(beatDuration);

  const waves = [
    // Pacing spike — narrow artefact preceding QRS
    { theta: -Math.PI / 6.5,  a:  22.0, b: 0.03 },
    // Wide R wave — LBBB-like, dominant positive deflection, no Q or S
    { theta:  0,              a:  18.0, b: 0.22 },
    // Discordant T wave — inverted, broad (repolarisation abnormality)
    { theta:  Math.PI / 2,    a:  -6.0, b: 0.45 },
  ];

  const samples = generateBaseECG(numSamples, waves);
  return normaliseWaveform(samples);
}

/**
 * CPR compression-artifact ECG buffer
 *
 * During CPR the ECG does not show cardiac electrical activity — it shows the
 * mechanical artifact produced by each chest compression (electrode movement +
 * capacitive coupling through the chest wall).  Each compression produces a
 * broad (~200 ms) sinusoidal deflection that repeats at the compression rate.
 *
 * High-quality CPR (compressionRate ≈ 110/min, isHighQuality = true):
 *   • Regular spikes at correct rate (100–120 bpm target)
 *   • Relatively high amplitude (good compression depth)
 *   • Modest baseline wander and noise
 *
 * Low-quality CPR (compressionRate ≈ 80/min, isHighQuality = false):
 *   • Rate too slow (< 100 bpm) — most common quality failure in sims
 *   • Lower amplitude (shallow compressions)
 *   • More baseline wander and noise (poor technique)
 *   • Periodic 2-cycle pauses (interruptions > 10 s — low CPR fraction)
 *
 * @param {number} sampleRate
 * @param {number} bufferDuration
 * @param {number} compressionRate — compressions per minute
 * @param {boolean} isHighQuality
 */
function generateCPRBuffer(sampleRate, bufferDuration, compressionRate, isHighQuality) {
  const N = sampleRate * bufferDuration;
  const buf = new Float32Array(N);
  const samplesPerCycle = (60 / compressionRate) * sampleRate;
  // Duty cycle: compression stroke takes ~35% of the period at 100 bpm (≈200 ms)
  const dutyCycles = 0.35;
  // Amplitude: high quality → full scale; low quality → ~55% (simulates shallow depth)
  const peakAmplitude = isHighQuality ? 1.0 : 0.55;
  // Baseline wander — low-frequency oscillation from patient body movement
  const wanderAmp  = isHighQuality ? 0.07 : 0.18;
  const wanderFreq = (compressionRate / 60) * 0.33; // sub-harmonic of compression rate
  // Noise floor
  const noiseAmp = isHighQuality ? 0.04 : 0.10;

  for (let i = 0; i < N; i++) {
    const t = i / sampleRate;
    const cyclePos  = i % samplesPerCycle;
    const cycleIdx  = Math.floor(i / samplesPerCycle);
    const phase     = cyclePos / samplesPerCycle; // 0..1 within this cycle

    // Low-quality: inject a 2-cycle pause every 8 compression cycles
    // (simulates CPR interruptions > 10 s — a quality failure metric)
    const isPause = !isHighQuality && (cycleIdx % 8 >= 6);

    let value = 0;
    if (!isPause && phase < dutyCycles) {
      // Broad sinusoidal deflection (upstroke + downstroke within the duty window)
      const strokePhase = phase / dutyCycles; // 0..1
      value = peakAmplitude * Math.sin(Math.PI * strokePhase);
    }

    // Baseline wander + noise always present
    const wander = wanderAmp * Math.sin(2 * Math.PI * wanderFreq * t + 0.4);
    const noise  = noiseAmp  * (Math.random() - 0.5);

    buf[i] = value + wander + noise;
  }

  // Normalise so the peak compression spike sits at +1.0
  let maxAbs = 0;
  for (let i = 0; i < N; i++) if (Math.abs(buf[i]) > maxAbs) maxAbs = Math.abs(buf[i]);
  if (maxAbs > 0) for (let i = 0; i < N; i++) buf[i] /= maxAbs;

  return buf;
}

// Electrical alternans: relative amplitude of the "small" beat vs the "big" beat.
const ALTERNANS_SCALE = 0.5;

/**
 * Scenarios/modifiers whose haemodynamics produce a marked respiratory swing in
 * pulse amplitude (systolic-pressure / pleth variability), the way severe
 * hypovolaemia or haemorrhage does. When any of these is active the SpO₂ pleth
 * and arterial trace get the same respiratory modulation used for tamponade.
 * Cardiac tamponade is handled separately via rhythm === 'tamponade'.
 */
export const HYPOVOLEMIC_MODIFIERS = new Set(['hypovolemia']);
export const HYPOVOLEMIC_SCENARIOS = new Set([
  'epicHemorrhage', 'obgynHemorrhagicShock', 'palsHypovolemicShock',
]);

/** Does the active modifier/scenario warrant a respiratory pulse-amplitude swing? */
export function hasRespiratorySwing(activeModifier, activeScenario) {
  return HYPOVOLEMIC_MODIFIERS.has(activeModifier) || HYPOVOLEMIC_SCENARIOS.has(activeScenario);
}

/**
 * Cardiac tamponade — arterial pulsus paradoxus.
 *
 * Builds the ABP buffer beat-by-beat, scaling each cardiac beat's PULSE
 * amplitude by a smooth respiratory envelope. The pulse is widest in expiration
 * and narrows on inspiration — an exaggerated version of the normal <10 mmHg
 * swing.
 *   ampScale(t) = 1 − depth·½·(1 − cos(2π·t / Tresp))   ∈ [1−depth, 1]
 * where Tresp = 60/RR seconds is the respiratory period.
 *
 * NB: the modulation is applied to the DISPLAY samples, not by lowering the
 * systolic fed to generateABPWaveform — that generator re-normalises each beat
 * to its own pulse pressure (`/(pp·0.75)`), which would cancel the swing out.
 * The samples are MAP-centred (0 = centreline), so multiplying by ampScale
 * narrows the pulse about the MAP — the correct look for a scaled a-line trace.
 */
function generatePulsusABPBuffer(sampleRate, bufferDuration, hr, rr, sys, dia, depth = 0.4) {
  const total = Math.round(bufferDuration * sampleRate);
  const buf = new Float32Array(total);
  const Tresp = 60 / Math.max(rr, 1);
  const DEPTH = depth;  // fraction of pulse amplitude the respiratory swing spans
  let i = 0;
  while (i < total) {
    const t = i / sampleRate;
    const ampScale = 1 - DEPTH * 0.5 * (1 - Math.cos((2 * Math.PI * t) / Tresp));
    const beat = generateABPWaveform(hr, sys, dia);
    const n = Math.min(beat.length, total - i);
    for (let k = 0; k < n; k++) buf[i + k] = beat[k] * ampScale;
    i += n;
  }
  return buf;
}

/**
 * Cardiac tamponade — pulsus paradoxus on the SpO₂ pleth.
 *
 * The plethysmographic counterpart of arterial pulsus: pulse amplitude waxes
 * and wanes with respiration (the physiologic basis of the "pleth variability
 * index"). Built beat-by-beat, scaling each pleth beat's amplitude by the same
 * respiratory envelope used for the arterial trace so the two channels swing in
 * phase. Uses a deeper modulation than the a-line — the pleth is a relative
 * (unscaled) trace, so a large visible swing reads as the classic finding.
 */
function generatePulsusPlethBuffer(sampleRate, bufferDuration, hr, rr, spo2, depth = 0.45) {
  const total = Math.round(bufferDuration * sampleRate);
  const buf = new Float32Array(total);
  const Tresp = 60 / Math.max(rr, 1);
  const DEPTH = depth;  // pleth variability envelope (a-line depth scaled up a touch)
  let i = 0;
  while (i < total) {
    const t = i / sampleRate;
    const ampScale = 1 - DEPTH * 0.5 * (1 - Math.cos((2 * Math.PI * t) / Tresp));
    const beat = generateSpO2Waveform(hr, spo2);
    const n = Math.min(beat.length, total - i);
    for (let k = 0; k < n; k++) buf[i + k] = beat[k] * ampScale;
    i += n;
  }
  return buf;
}

/**
 * Generate a pre-computed sample buffer for a SINGLE rhythm/channel. The public
 * entry point is generateWaveformBuffer, which cross-fades between two rhythms
 * during a transition; this is the per-rhythm generator it calls.
 *
 * @param {string} waveformType    — 'ecg' | 'spo2' | 'capno' | 'abp' | 'cvp'
 * @param {object} vitals          — current vital signs from the store
 * @param {number} sampleRate      — samples per second (default 250)
 * @param {number} bufferDuration  — seconds of samples (default 6; capno uses 12)
 * @param {string} rhythm          — 'normal' | 'vf' | 'asystole' | 'cpr_hq' | 'cpr_lq' | 'vtach' | 'afib' | 'svt' | 'st_depression' | 'st_elevation' | 'hyperkalemia' | 'hypothermia' | 'tamponade'
 * @param {string} capnoMorphology — 'normal' | 'rebreathing' | 'curare_cleft' | 'obstructed'
 */
function generateRhythmBuffer(waveformType, vitals, sampleRate = SAMPLE_RATE, bufferDuration = 6, rhythm = 'normal', capnoMorphology = 'normal', respSwing = false, ppvDepth = 0) {
  const bufferSamples = bufferDuration * sampleRate;

  // ── VF / Cardiac Arrest rhythm ────────────────────────────────────────
  if (rhythm === 'vf') {
    if (waveformType === 'ecg') {
      return generateVFBuffer(sampleRate, bufferDuration);
    }
    // All other channels flatline during VF (no cardiac output)
    return generateFlatlineBuffer(sampleRate, bufferDuration);
  }

  // ── Pulseless VT — organised wide-complex ECG, NO cardiac output ──────
  // The monomorphic VT ECG persists (still a shockable arrest), but there is no
  // perfusion: the SpO₂ pleth, arterial line and CVP traces flatline. Distinct
  // from plain 'vtach' (VT WITH a weak pulse). Capnography is left to fall
  // through to normal generation, driven by the (now arrest-level) EtCO₂/RR.
  if (rhythm === 'vtach_pulseless') {
    if (waveformType === 'ecg') {
      return generateVTachBuffer(sampleRate, bufferDuration, vitals.HR?.current || 180);
    }
    if (waveformType === 'spo2' || waveformType === 'abp' || waveformType === 'cvp') {
      return generateFlatlineBuffer(sampleRate, bufferDuration);
    }
    // capno continues to the normal per-cycle generation below
  }

  // ── Asystole — true electrical silence across every channel ──────────
  // Distinct from VF: no chaotic ECG activity. All channels return a flat
  // line. PEA (organised rhythm + no output) uses rhythm='normal' instead,
  // preserving the ECG morphology while haemodynamic vitals are set to 0.
  if (rhythm === 'asystole') {
    return generateFlatlineBuffer(sampleRate, bufferDuration);
  }

  // ── CPR compression artifact ──────────────────────────────────────────
  // ECG shows mechanical compression artifact, not cardiac electrical activity.
  //   cpr_hq — 110 compressions/min, high amplitude, regular (good quality)
  //   cpr_lq — 80 compressions/min, low amplitude, with pauses (poor quality)
  // SpO₂, ABP, CVP channels fall through to normal generation driven by the
  // scenario vitals (SpO₂=40 → nearly-flat pleth; ABP at compression-driven values).
  // Capno generates normally at the scenario RR (bag-mask ventilation rate).
  if (rhythm === 'cpr_hq' && waveformType === 'ecg') {
    return generateCPRBuffer(sampleRate, bufferDuration, 110, true);
  }
  if (rhythm === 'cpr_lq' && waveformType === 'ecg') {
    return generateCPRBuffer(sampleRate, bufferDuration, 80, false);
  }
  // Mechanical CPR (LUCAS): machine-regular compressions at exactly 102/min —
  // no fatigue, no pauses; other channels fall through to the scenario vitals.
  if (rhythm === 'cpr_mech' && waveformType === 'ecg') {
    return generateCPRBuffer(sampleRate, bufferDuration, 102, true);
  }

  // ── AFib — whole-buffer generation (irregularly irregular QRS) ────────
  // Only the ECG channel needs special handling; SpO₂/ABP/CVP/capno
  // are driven by the store HR and fall through to normal tiling below.
  if (rhythm === 'afib' && waveformType === 'ecg') {
    const afibHR = vitals.HR?.current || 130;
    return generateAFibBuffer(sampleRate, bufferDuration, afibHR);
  }

  // ── Normal / VTach / ST changes ───────────────────────────────────────
  const buffer = new Float32Array(bufferSamples);

  let hr = vitals.HR?.current || 72;
  let rr = vitals.RR?.current || 14;
  let spo2 = vitals.SpO2?.current || 99;
  let etco2 = vitals.EtCO2?.current || 38;
  let abpSys = vitals.ABP_Sys?.current || 118;
  let abpDia = vitals.ABP_Dia?.current || 76;
  let cvp = vitals.CVP?.current || 4;

  // Guard: a sub-physiologic rate (arrest, or the asymptotic tail of a
  // transition toward 0 which leaves HR/RR as a tiny positive value like 1e-53)
  // must flatline rate-dependent channels. Using `< 1` rather than `<= 0` is
  // critical: a per-beat generator computes numSamples = (60/rate)·sampleRate,
  // so a near-zero rate would request a gigantic typed array → RangeError.
  if (hr < 1 && (waveformType === 'ecg' || waveformType === 'spo2' || waveformType === 'abp' || waveformType === 'cvp')) {
    return buffer; // all zeros
  }
  if (rr < 1 && waveformType === 'capno') {
    return buffer;
  }

  // ── VTach ECG — whole-buffer generation (continuous-time formula) ──────
  if (rhythm === 'vtach' && waveformType === 'ecg') {
    return generateVTachBuffer(sampleRate, bufferDuration, hr);
  }

  // ── TdP ECG — amplitude-modulated whole-buffer generation ────────────
  if (rhythm === 'tdp' && waveformType === 'ecg') {
    return generateTdPBuffer(sampleRate, bufferDuration, hr);
  }

  // ── Respiratory pulse-amplitude variation (pulsus paradoxus / SPV/PPV) ──
  // Pulse amplitude waxes and wanes with the RESPIRATORY cycle. Sources here:
  //   • cardiac tamponade (rhythm === 'tamponade'),
  //   • severe hypovolaemia / haemorrhage (respSwing flag),
  //   • an instructor-set Pulse Pressure Variation (ppvDepth).
  // All of them drive BOTH the arterial line (PPV/SPV) and the SpO₂ pleth (the
  // pleth-variability index), which move in phase. Whole-buffer generation is
  // required because the modulation period is the respiratory cycle, not the
  // cardiac one.
  const scenarioDepth = (rhythm === 'tamponade' || respSwing) ? 0.4 : 0;
  const swingDepth = Math.max(scenarioDepth, ppvDepth || 0);
  if (swingDepth > 0 && waveformType === 'abp') {
    return generatePulsusABPBuffer(sampleRate, bufferDuration, hr, rr, abpSys, abpDia, swingDepth);
  }
  if (swingDepth > 0 && waveformType === 'spo2') {
    return generatePulsusPlethBuffer(sampleRate, bufferDuration, hr, rr, spo2, swingDepth + 0.05);
  }

  let cycleSamples;

  switch (waveformType) {
    case 'ecg':
      // Select ECG morphology based on rhythm
      if (rhythm === 'svt') {
        cycleSamples = generateSVTWaveform(hr);
      } else if (rhythm === 'st_depression') {
        cycleSamples = generateSTDepressionWaveform(hr);
      } else if (rhythm === 'st_elevation') {
        cycleSamples = generateSTElevationWaveform(hr);
      } else if (rhythm === 'hyperkalemia') {
        cycleSamples = generateHyperkalemiaWaveform(hr);
      } else if (rhythm === 'hypothermia') {
        cycleSamples = generateHypothermiaWaveform(hr);
      } else if (rhythm === 'paced') {
        cycleSamples = generatePacedECGWaveform(hr);
      } else if (rhythm === 'tamponade') {
        // Electrical alternans: beat-to-beat QRS amplitude variation as the
        // heart swings within the pericardial effusion. Build a TWO-beat cycle
        // (full-amplitude beat + reduced-amplitude beat); tiling the pair
        // reproduces the alternating morphology with no whole-buffer logic.
        const beat = generateECGWaveform(hr);
        const pair = new Float32Array(beat.length * 2);
        pair.set(beat, 0);
        for (let i = 0; i < beat.length; i++) pair[beat.length + i] = beat[i] * ALTERNANS_SCALE;
        cycleSamples = pair;
      } else {
        cycleSamples = generateECGWaveform(hr);
      }
      break;
    case 'spo2':
      cycleSamples = generateSpO2Waveform(hr, spo2);
      break;
    case 'capno':
      if (capnoMorphology === 'rebreathing') {
        cycleSamples = generateRebreathingCapno(rr, etco2);
      } else if (capnoMorphology === 'curare_cleft') {
        cycleSamples = generateCurareCleftCapno(rr, etco2);
      } else if (capnoMorphology === 'obstructed') {
        cycleSamples = generateObstructedCapno(rr, etco2);
      } else {
        cycleSamples = generateCapnographyWaveform(rr, etco2);
      }
      break;
    case 'abp':
      // IABP: diastolic augmentation morphology replaces the native trace.
      cycleSamples = rhythm === 'iabp'
        ? generateIABPWaveform(hr, abpSys, abpDia)
        : generateABPWaveform(hr, abpSys, abpDia);
      break;
    case 'cvp':
      cycleSamples = generateCVPWaveform(hr, cvp);
      break;
    default:
      cycleSamples = new Float32Array(sampleRate);
  }

  // Unstable VT/TdP perfuse poorly — damp the pulsatile traces to a weak, thready
  // pulse so the display doesn't imply healthy perfusion. Both the SpO₂ pleth and
  // the arterial line self-centre on their baseline (0 / MAP), so a straight
  // amplitude scale narrows the visible pulse without shifting the baseline. (Truly
  // pulseless VT is modelled via the VF/arrest path, which flatlines these channels.)
  if ((rhythm === 'vtach' || rhythm === 'tdp') && (waveformType === 'spo2' || waveformType === 'abp')) {
    const DAMP = waveformType === 'spo2' ? 0.28 : 0.32;
    for (let i = 0; i < cycleSamples.length; i++) cycleSamples[i] *= DAMP;
  }

  // Impella: continuous axial flow partially unloads the LV — the arterial trace
  // (and pleth) keep only a small pulsatile component around MAP. The ABP
  // generator self-normalises to pulse pressure, so scale the drawn amplitude
  // down or a PP of 15 would render as tall as a native trace.
  if (rhythm === 'impella' && (waveformType === 'abp' || waveformType === 'spo2')) {
    const DAMP = waveformType === 'abp' ? 0.25 : 0.45;
    for (let i = 0; i < cycleSamples.length; i++) cycleSamples[i] *= DAMP;
  }

  // Tile the cycle across the buffer
  let index = 0;
  while (index < bufferSamples) {
    const remaining = bufferSamples - index;
    const copyLength = Math.min(cycleSamples.length, remaining);
    buffer.set(cycleSamples.subarray(0, copyLength), index);
    index += copyLength;
  }

  return buffer;
}

/**
 * Public waveform generator — makes rhythm changes GRADUAL.
 *
 * When the rhythm changes, the monitor cross-fades from the previous rhythm's
 * buffer to the new one over the transition window (progress 0 → 1). Both
 * per-beat buffers are generated at the current heart rate, so their beats are
 * time-aligned: a linear sample blend of two aligned beats interpolates the
 * morphology point-by-point (e.g. normal → hyperkalemia: the P wave fades and
 * the T wave tents; and the reverse on recovery). For whole-buffer rhythms
 * (VF, VTach…) it is a smooth amplitude fade. Channels whose output doesn't
 * depend on rhythm produce identical from/to buffers, so they're unaffected —
 * except the pulse channels near VF/asystole, which fade out as output is lost.
 *
 * @param {string} prevRhythm      — the rhythm being transitioned FROM
 * @param {number} rhythmProgress  — 0 (fully prevRhythm) … 1 (fully rhythm)
 */
export function generateWaveformBuffer(waveformType, vitals, sampleRate = SAMPLE_RATE, bufferDuration = 6, rhythm = 'normal', capnoMorphology = 'normal', prevRhythm = rhythm, rhythmProgress = 1, respSwing = false, ppvDepth = 0) {
  const to = generateRhythmBuffer(waveformType, vitals, sampleRate, bufferDuration, rhythm, capnoMorphology, respSwing, ppvDepth);
  // No transition in progress → just the target rhythm.
  if (prevRhythm === rhythm || rhythmProgress >= 1) return to;

  const from = generateRhythmBuffer(waveformType, vitals, sampleRate, bufferDuration, prevRhythm, capnoMorphology, respSwing, ppvDepth);
  const a = Math.max(0, Math.min(1, rhythmProgress));
  const out = new Float32Array(to.length);
  for (let i = 0; i < to.length; i++) {
    const f = i < from.length ? from[i] : 0;
    out[i] = f * (1 - a) + to[i] * a;
  }
  return out;
}
